import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, ArrowRight } from '../icons/StatboxIcons'
import supabase from '../../supabaseClient'
import {
  clearVenueSignupBooking,
  readDraft,
  resolveVenueBookedAtForUser,
  writeDraft,
} from './signupState'
import { signOutSignupIntake } from '../../utils/signOutSignupFlow'
import { persistSignupUserOnActivationCallBooked } from '../../lib/signupUserPersist'
import { BookDemoScheduler } from '@/components/BookDemoScheduler'

/**
 * Venue signup — same layout as academy: story left, Calendly booking right.
 */

const COURT_RANGES = [
  { id: '1_2', label: '1–2 courts' },
  { id: '3_5', label: '3–5 courts' },
  { id: '6_10', label: '6–10 courts' },
  { id: '10_plus', label: '10+ courts' },
]

const EXPECT_ITEMS = [
  'Walk through venue setup and cameras',
  'Custom pricing for your courts',
  'Hardware options (Statbox smart cameras)',
  'Booking sync and player engagement',
]

export default function SignupVenueBook() {
  const navigate = useNavigate()
  const initial = useMemo(readDraft, [])
  const initialVenue = initial.venue || {}

  const [name, setName] = useState(initial.fullName || '')
  const [email, setEmail] = useState(initial.email || '')
  const [bookedAt, setBookedAt] = useState('')
  const [courtRange, setCourtRange] = useState(initialVenue.courtRange || '')
  const [bookingChecked, setBookingChecked] = useState(false)

  const handleCourtRangeChange = useCallback((id) => {
    setCourtRange(id)
    const draft = readDraft()
    writeDraft({ venue: { ...draft.venue, courtRange: id } })
  }, [])

  const bookingCardRef = useRef(null)
  const scrollToBooking = useCallback(() => {
    bookingCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  useEffect(() => {
    writeDraft({
      persona: 'venue',
      signupStep: Math.max(2, Number(initial.signupStep || 0)),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let cancelled = false

    async function hydrateFromSession() {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (cancelled) return

      if (!session?.user) {
        clearVenueSignupBooking()
        setBookedAt('')
        setBookingChecked(true)
        return
      }

      const resolvedBookedAt = resolveVenueBookedAtForUser(session.user.id)
      setBookedAt(resolvedBookedAt)

      if (resolvedBookedAt) {
        const draft = readDraft()
        await persistSignupUserOnActivationCallBooked({
          persona: 'venue',
          fullName: draft.fullName || '',
          email: draft.email || session.user.email || '',
          phone: draft.phone || '',
          organizationName: draft.venue?.name || '',
          bookedAt: resolvedBookedAt,
          onboardingData: { court_range: draft.venue?.courtRange || '' },
        })
      }

      if (session.user.email) setEmail((prev) => prev || session.user.email || '')
      const meta = session.user.user_metadata || {}
      const fromMeta = meta.full_name || meta.name || ''
      if (fromMeta) setName((prev) => prev || fromMeta)

      setBookingChecked(true)
    }

    hydrateFromSession()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        clearVenueSignupBooking()
        setBookedAt('')
      }
      if (event === 'SIGNED_IN' && session?.user) {
        setBookedAt(resolveVenueBookedAtForUser(session.user.id))
      }
    })

    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => {
      writeDraft({
        fullName: name.trim(),
        email: email.trim(),
      })
    }, 400)
    return () => clearTimeout(t)
  }, [name, email])

  const persistUsersRow = useCallback(
    async ({ iso, lead, draft }) => {
      const venueName = lead?.organization || draft.venue?.name || ''
      const persist = await persistSignupUserOnActivationCallBooked({
        persona: 'venue',
        fullName: lead?.name || name,
        email: lead?.email || email,
        phone: lead?.phone || '',
        organizationName: venueName,
        bookedAt: iso,
        onboardingData: {
          court_range: lead?.teamSize ?? draft.venue?.courtRange ?? '',
        },
      })
      if (!persist.ok) {
        console.warn('SignupVenueBook: Users row not saved', persist.error)
      }
    },
    [name, email],
  )

  const handleBooked = useCallback(async (result, lead) => {
    const iso = result.start_time || new Date().toISOString()
    const {
      data: { session },
    } = await supabase.auth.getSession()
    const userId = session?.user?.id || ''
    const draft = readDraft()
    const nextName = lead?.name || name
    const nextEmail = lead?.email || email
    const venueName = lead?.organization || draft.venue?.name || ''
    writeDraft({
      fullName: nextName,
      email: nextEmail,
      phone: lead?.phone?.trim() || draft.phone || '',
      persona: 'venue',
      accountStatus: 'setup_mode',
      activationCallBooked: true,
      activationCallBookedAt: iso,
      venue: {
        ...draft.venue,
        name: venueName,
        courtRange: lead?.teamSize ?? draft.venue?.courtRange ?? '',
        bookedAt: iso,
        bookedByUserId: userId,
      },
    })
    await persistUsersRow({ iso, lead, draft: readDraft() })
    if (lead?.name) setName(lead.name)
    if (lead?.email) setEmail(lead.email)
    setBookedAt(iso)
  }, [name, email, persistUsersRow])

  if (!bookingChecked) {
    return (
      <PageShell>
        <div className="flex flex-1 items-center justify-center py-24">
          <p className="text-sm text-[var(--signup-text-secondary)]">Loading…</p>
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <div className="w-full max-w-[1180px] mx-auto px-6 sm:px-10 pt-6 sm:pt-10 pb-28 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-[55fr_45fr] gap-10 lg:gap-20 items-start">
          <div className="min-w-0 flex flex-col">
            <h1
              className="font-semibold text-[var(--signup-text-primary)] text-[28px] sm:text-[30px] lg:text-[36px] leading-[1.15]"
              style={{ letterSpacing: '-0.01em' }}
            >
              Let&apos;s set up your venue together.
            </h1>
            <p className="mt-4 text-[16px] text-[var(--signup-text-secondary)] leading-[1.55] max-w-[480px]">
              Venues need a real conversation. Book a 20-min call and
              we&apos;ll walk through setup, pricing, and how Statbox fits
              your courts.
            </p>

            <section className="mt-14" aria-labelledby="venue-expect-heading">
              <h2
                id="venue-expect-heading"
                className="text-[12px] font-medium uppercase text-[var(--signup-text-tertiary)]"
                style={{ letterSpacing: '0.14em' }}
              >
                What to expect
              </h2>
              <ul className="mt-4 space-y-2">
                {EXPECT_ITEMS.map((item) => (
                  <li
                    key={item}
                    className="flex items-baseline gap-3 text-[13px] text-[var(--signup-text-primary)] leading-[1.5]"
                  >
                    <span
                      aria-hidden
                      className="text-[var(--signup-text-tertiary)] select-none"
                    >
                      —
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <p className="mt-8 text-[12px] text-[var(--signup-text-tertiary)] leading-[1.55]">
              Prefer email? Reach us at{' '}
              <a
                href="mailto:support@statbox.co"
                className="text-[var(--signup-accent)] hover:underline underline-offset-4 motion-safe:transition-colors motion-safe:duration-150"
              >
                support@statbox.co
              </a>
            </p>
          </div>

          <div className="min-w-0">
            {bookedAt ? (
              <BookingConfirmedCard />
            ) : (
              <div
                ref={bookingCardRef}
                className="rounded-2xl border border-[var(--signup-border-subtle)] bg-white p-6 sm:p-8 scroll-mt-6"
                style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)' }}
              >
                <h2
                  className="text-[24px] font-semibold text-[var(--signup-text-primary)]"
                  style={{ letterSpacing: '-0.01em' }}
                >
                  When are you free?
                </h2>
                <p className="mt-2 text-[13px] text-[var(--signup-text-secondary)]">
                  Twenty minutes, just a video chat. We'll send the invite to your inbox.
                </p>
                <div className="mt-6">
                  <BookDemoScheduler
                    audience="venue"
                    variant="light"
                    layout="stacked"
                    name={name}
                    email={email}
                    onNameChange={setName}
                    onEmailChange={setEmail}
                    onBooked={handleBooked}
                    defaultOrganization={initialVenue.name || ''}
                    confirmLabel="Book this time"
                    footerNote="No pitch. Just a walkthrough of the app."
                    organizationPlaceholder="Venue name"
                    teamSizeOptions={COURT_RANGES}
                    teamSize={courtRange}
                    onTeamSizeChange={handleCourtRangeChange}
                    teamSizeSectionLabel="Roughly how many courts do you have?"
                    lockContactFields
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {!bookedAt && (
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-10 bg-[var(--signup-bg-base)] border-t border-[var(--signup-border-subtle)] px-5 pt-4"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={scrollToBooking}
            className="w-full h-[52px] rounded-xl bg-[var(--signup-accent)] text-white font-medium text-[15px] inline-flex items-center justify-center gap-2 hover:bg-[var(--signup-accent-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--signup-bg-base)] motion-safe:transition-colors motion-safe:duration-150 motion-safe:active:scale-[0.99]"
          >
            Find a time
            <ArrowRight size={18} strokeWidth={2} aria-hidden />
          </button>
        </div>
      )}
    </PageShell>
  )
}

function BookingConfirmedCard() {
  return (
    <div
      role="status"
      className="rounded-2xl border border-[var(--signup-border-subtle)] bg-[var(--signup-bg-elevated)] p-8 sm:p-10"
      style={{ boxShadow: 'var(--signup-shadow-sm)' }}
    >
      <CheckCircle2
        size={32}
        strokeWidth={1.75}
        className="text-[var(--signup-accent)]"
        aria-hidden
      />
      <h3 className="mt-4 text-[18px] font-semibold text-[var(--signup-text-primary)]">
        You&apos;re in — see you soon.
      </h3>
      <p className="mt-2 text-[14px] text-[var(--signup-text-secondary)] leading-[1.55]">
        Check your email for the calendar invite. Looking forward to meeting you.
      </p>
      <Link
        to="/"
        className="mt-7 inline-flex items-center gap-2 h-[44px] px-5 rounded-lg bg-[var(--signup-accent)] text-white font-medium text-[14px] hover:bg-[var(--signup-accent-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--signup-bg-base)] motion-safe:transition-[background-color,box-shadow] motion-safe:duration-150"
        style={{ boxShadow: 'var(--signup-shadow-sm)' }}
      >
        Back to Statbox
        <ArrowRight size={16} aria-hidden />
      </Link>
    </div>
  )
}

function PageShell({ children }) {
  const navigate = useNavigate()
  const [backBusy, setBackBusy] = useState(false)

  const handleBack = async () => {
    if (backBusy) return
    setBackBusy(true)
    try {
      await signOutSignupIntake()
      navigate('/signup/account', { replace: true })
    } catch (err) {
      console.warn('SignupVenueBook: sign-out on back failed', err)
    } finally {
      setBackBusy(false)
    }
  }

  return (
    <div
      className="min-h-screen min-h-[100svh] font-sans antialiased flex flex-col bg-[var(--signup-bg-base)] text-[var(--signup-text-primary)]"
      style={{
        fontFeatureSettings: '"cv11", "ss03", "cv05"',
        textRendering: 'optimizeLegibility',
      }}
    >
      <header className="h-20 flex items-center justify-between px-6 sm:px-10 shrink-0">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleBack}
            disabled={backBusy}
            aria-label="Back"
            className="-ml-2.5 inline-flex h-11 w-11 items-center justify-center rounded-sm text-[var(--signup-text-secondary)] hover:text-[var(--signup-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-border-strong)] motion-safe:transition-colors motion-safe:duration-150 disabled:opacity-50"
          >
            <ArrowLeft size={20} strokeWidth={1.75} aria-hidden />
          </button>
          <Link
            to="/"
            aria-label="Statbox home"
            className="flex items-center gap-2"
          >
            <img
              src="/Logo_Statbox_Transparent.png"
              alt=""
              className="h-7 w-auto"
            />
            <img
              src="/logo_white.png"
              alt="Statbox"
              className="h-4 w-auto opacity-90"
              style={{ filter: 'invert(1)' }}
            />
          </Link>
        </div>
        <Link
          to="/venue-owner/login"
          className="hidden md:inline-block text-[13px] text-[var(--signup-text-secondary)] hover:text-[var(--signup-text-primary)] motion-safe:transition-colors motion-safe:duration-150"
        >
          Already have an account?{' '}
          <span className="text-[var(--signup-accent)] font-medium">Log in</span>
        </Link>
      </header>

      <div className="flex-1 flex flex-col">{children}</div>
    </div>
  )
}
